import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";

export default {
  name: "RSAIDVerificationReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    rsa_id_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        personal_information: false,
        document_details: false,
        verification_results: false,
        system_status: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    // Safely access the response data
    safe_rsa_id_data() {
      return this.rsa_id_data || {};
    },

    // Get the actual response data from the nested structure
    result_data() {
      // The actual data is in response.response based on your structure
      if (this.safe_rsa_id_data.response && this.safe_rsa_id_data.response.response) {
        return this.safe_rsa_id_data.response.response;
      }
      // Fallback to response if structure is different
      if (this.safe_rsa_id_data.response) {
        return this.safe_rsa_id_data.response;
      }
      // Fallback to root level data
      return this.safe_rsa_id_data;
    },

    // Service information
    service_id() {
      return this.safe_rsa_id_data.service_id || "N/A";
    },

    service_name() {
      return this.safe_rsa_id_data.service_name || "RSA ID Verification";
    },

    // API Response metadata
    api_status() {
      if (this.safe_rsa_id_data.response && this.safe_rsa_id_data.response.status) {
        return this.safe_rsa_id_data.response.status;
      }
      return 200;
    },

    api_title() {
      if (this.safe_rsa_id_data.response && this.safe_rsa_id_data.response.title) {
        return this.safe_rsa_id_data.response.title;
      }
      return "Citizen Api Output";
    },

    api_detail() {
      if (this.safe_rsa_id_data.response && this.safe_rsa_id_data.response.detail) {
        return this.safe_rsa_id_data.response.detail;
      }
      return "Details of the output";
    },

    // Report metadata
    report_reference() {
      return this.result_data.CRef || `RSA-ID-${Date.now().toString().slice(-8)}`;
    },

    generated_date() {
      return new Date().toLocaleDateString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    // Personal Information
    first_name() {
      return this.result_data.FirstName || "N/A";
    },

    last_name() {
      return this.result_data.LastName || "N/A";
    },

    full_name() {
      return `${this.first_name} ${this.last_name}`;
    },

    id_number() {
      return this.result_data.IdNumber || "N/A";
    },

    marital_status() {
      return this.result_data.MaritalStatus || "N/A";
    },

    date_of_birth() {
      if (this.id_number && this.id_number !== "N/A" && this.id_number.length >= 6) {
        const year = this.id_number.substring(0, 2);
        const month = this.id_number.substring(2, 4);
        const day = this.id_number.substring(4, 6);
        const currentYear = new Date().getFullYear();
        const century = currentYear - (currentYear % 100);
        const fullYear = parseInt(year) + (parseInt(year) > (currentYear % 100) ? century - 100 : century);
        return `${day}/${month}/${fullYear}`;
      }
      return "N/A";
    },

    gender() {
      if (this.id_number && this.id_number !== "N/A" && this.id_number.length >= 10) {
        const genderDigit = parseInt(this.id_number.substring(6, 10));
        return genderDigit < 5000 ? "Female" : "Male";
      }
      return "N/A";
    },

    // Document Details
    smart_card_issued() {
      return this.result_data.SmartCardIssued || false;
    },

    smart_card_status() {
      return this.smart_card_issued ? "Issued" : "Not Issued";
    },

    id_issue_date() {
      return this.result_data.IDIssueDate || "N/A";
    },

    formatted_id_issue_date() {
      return this.format_date(this.id_issue_date);
    },

    id_sequence_number() {
      return this.result_data.IDSequenceNumber || "N/A";
    },

    birth_place_country() {
      return this.result_data.BirthPlaceCountry || "N/A";
    },

    birth_place_country_code() {
      return this.result_data.BirthPlaceCountryCode || "N/A";
    },

    birth_place() {
      return this.birth_place_country;
    },

    citizenship_status() {
      if (this.id_number && this.id_number !== "N/A" && this.id_number.length >= 11) {
        const citizenshipDigit = parseInt(this.id_number.charAt(10));
        return citizenshipDigit === 0 ? "SA Citizen" : "Permanent Resident";
      }
      return "N/A";
    },

    // Status Indicators
    dead_indicator() {
      return this.result_data.DeadIndicator || false;
    },

    dead_indicator_text() {
      return this.dead_indicator ? "Yes" : "No";
    },

    dead_indicator_class() {
      return this.dead_indicator ? "text-danger" : "text-success";
    },

    date_of_death() {
      return this.result_data.DateOfDeath || null;
    },

    formatted_date_of_death() {
      return this.date_of_death ? this.format_date(this.date_of_death) : "N/A";
    },

    id_blocked() {
      return this.result_data.IDBlocked || false;
    },

    id_blocked_text() {
      return this.id_blocked ? "Yes" : "No";
    },

    id_blocked_class() {
      return this.id_blocked ? "text-danger" : "text-success";
    },

    on_hanis() {
      return this.result_data.OnHanis || false;
    },

    on_hanis_text() {
      return this.on_hanis ? "Yes" : "No";
    },

    on_hanis_class() {
      return this.on_hanis ? "text-success" : "text-warning";
    },

    on_npr() {
      return this.result_data.OnNPR || false;
    },

    on_npr_text() {
      return this.on_npr ? "Yes" : "No";
    },

    on_npr_class() {
      return this.on_npr ? "text-success" : "text-warning";
    },

    is_wallet_created() {
      return this.result_data.IsWalletCreated || false;
    },

    wallet_created_text() {
      return this.is_wallet_created ? "Yes" : "No";
    },

    // Verification Results
    first_name_result() {
      return this.result_data.FirstNameResult || "0";
    },

    last_name_result() {
      return this.result_data.LastNameResult || "0";
    },

    first_name_match_class() {
      const score = parseFloat(this.first_name_result);
      if (score >= 80) return "bg-success";
      if (score >= 50) return "bg-warning text-dark";
      return "bg-danger";
    },

    last_name_match_class() {
      const score = parseFloat(this.last_name_result);
      if (score >= 80) return "bg-success";
      if (score >= 50) return "bg-warning text-dark";
      return "bg-danger";
    },

    // Overall Status - Determine based on name matches and other factors
    overall_status() {
      const firstNameScore = parseFloat(this.first_name_result);
      const lastNameScore = parseFloat(this.last_name_result);
      
      // Check if verification is successful based on multiple criteria
      if (firstNameScore >= 80 && lastNameScore >= 80 && !this.dead_indicator && !this.id_blocked) {
        return "success";
      }
      return "failed";
    },

    overall_status_text() {
      return this.overall_status === "success" ? "Verified" : "Failed";
    },

    overall_status_class() {
      return this.overall_status === "success" ? "bg-success" : "bg-danger";
    },

    verification_status_text() {
      if (this.overall_status === "success") {
        return "Identity Successfully Verified";
      }
      return "Identity Verification Failed";
    },

    verification_status_class() {
      return this.overall_status === "success" ? "text-success" : "text-danger";
    },

    // Images
    facial_image_available() {
      return this.result_data.FacialImageAvailable || false;
    },

    facial_image() {
      return this.result_data.FacialImage || null;
    },

    facial_image_src() {
      if (!this.facial_image || !this.facial_image_available) return null;
      if (this.facial_image.startsWith("data:")) return this.facial_image;
      return `data:image/jpeg;base64,${this.facial_image}`;
    },

    // Additional Information
    tracking_number() {
      return this.result_data.TrackingNumber || "N/A";
    },

    cached_result() {
      return this.result_data.CachedResult || false;
    },

    cache_date() {
      return this.result_data.CacheDate || null;
    },

    formatted_cache_date() {
      return this.cache_date ? this.format_date(this.cache_date) : "N/A";
    },

    cref() {
      return this.result_data.CRef || "N/A";
    },

    message() {
      return this.result_data.Message || "N/A";
    },

    result_status() {
      return this.result_data.Status || "N/A";
    },

    // Date of Marriage
    date_of_marriage() {
      return this.result_data.DateOfMarriage || null;
    },

    formatted_date_of_marriage() {
      return this.date_of_marriage ? this.format_date(this.date_of_marriage) : "N/A";
    },

    // Derived Information
    age() {
      if (this.date_of_birth !== "N/A") {
        const parts = this.date_of_birth.split('/');
        const birthDate = new Date(parts[2], parts[1] - 1, parts[0]);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age;
      }
      return "N/A";
    },

    // Export validation
    has_selected_tables() {
      return Object.values(this.export_options).some((selected) => selected);
    },
  },
  methods: {
    format_date(dateString) {
      if (!dateString || dateString === "N/A") return "N/A";
      try {
        // Handle YYYYMMDD format
        if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
          const year = dateString.substring(0, 4);
          const month = dateString.substring(4, 6);
          const day = dateString.substring(6, 8);
          const date = new Date(year, month - 1, day);
          return date.toLocaleDateString("en-GB");
        }
        
        // Handle other date formats
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("en-GB");
      } catch (error) {
        return dateString;
      }
    },

    show_export_modal() {
      const modalId = `export-modal-${this.report_reference}`;
      $(`#${modalId}`).modal("show");
    },

    close_modal() {
      const modalId = `export-modal-${this.report_reference}`;
      $(`#${modalId}`).modal("hide");
    },

    async load_libraries() {
      if (this.export_format === "csv" && !this.libraries_loaded.papa_parse) {
        await this.load_papa_parse();
      }
      if (this.export_format === "excel" && !this.libraries_loaded.sheetjs) {
        await this.load_sheetjs();
      }
    },

    load_papa_parse() {
      return new Promise((resolve, reject) => {
        if (window.Papa) {
          this.libraries_loaded.papa_parse = true;
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = "https://unpkg.com/papaparse@5.3.2/papaparse.min.js";
        script.onload = () => {
          this.libraries_loaded.papa_parse = true;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    load_sheetjs() {
      return new Promise((resolve, reject) => {
        if (window.XLSX) {
          this.libraries_loaded.sheetjs = true;
          resolve();
          return;
        }

        const script = document.createElement("script");
                script.src = "https://unpkg.com/xlsx@0.18.5/dist/xlsx.full.min.js";
        script.onload = () => {
          this.libraries_loaded.sheetjs = true;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    prepare_personal_information_data() {
      return [
        { field: "First Name", value: this.first_name },
        { field: "Last Name", value: this.last_name },
        { field: "Full Name", value: this.full_name },
        { field: "ID Number", value: this.id_number },
        { field: "Date of Birth", value: this.date_of_birth },
        { field: "Age", value: `${this.age} years` },
        { field: "Gender", value: this.gender },
        { field: "Marital Status", value: this.marital_status },
        { field: "Birth Place", value: this.birth_place },
        { field: "Birth Country Code", value: this.birth_place_country_code },
        { field: "Citizenship Status", value: this.citizenship_status },
      ];
    },

    prepare_document_details_data() {
      return [
        { field: "Smart Card Status", value: this.smart_card_status },
        { field: "ID Issue Date", value: this.formatted_id_issue_date },
        { field: "ID Sequence Number", value: this.id_sequence_number },
        { field: "Facial Image Available", value: this.facial_image_available ? "Yes" : "No" },
        { field: "Tracking Number", value: this.tracking_number },
        { field: "Reference Number", value: this.cref },
        { field: "Service Name", value: this.service_name },
        { field: "Service ID", value: this.service_id },
      ];
    },

    prepare_verification_results_data() {
      return [
        { 
          check: "First Name Match", 
          result: this.first_name_result + "%", 
          status: parseFloat(this.first_name_result) >= 80 ? "Pass" : "Fail" 
        },
        { 
          check: "Last Name Match", 
          result: this.last_name_result + "%", 
          status: parseFloat(this.last_name_result) >= 80 ? "Pass" : "Fail" 
        },
        { 
          check: "Overall Verification", 
          result: this.overall_status_text, 
          status: this.overall_status === "success" ? "Pass" : "Fail" 
        },
        {
          check: "API Response Status",
          result: this.api_status === 200 ? "Success" : "Error",
          status: this.api_status === 200 ? "Pass" : "Fail"
        },
      ];
    },

    prepare_system_status_data() {
      return [
        { 
          system: "Dead Indicator", 
          status: this.dead_indicator_text, 
          alert: this.dead_indicator ? "Alert" : "Clear",
          details: this.dead_indicator ? `Date of Death: ${this.formatted_date_of_death}` : "Person is alive"
        },
        { 
          system: "ID Blocked", 
          status: this.id_blocked_text, 
          alert: this.id_blocked ? "Alert" : "Clear",
          details: this.id_blocked ? "ID is blocked or restricted" : "ID is active"
        },
        { 
          system: "On HANIS", 
          status: this.on_hanis_text, 
          alert: this.on_hanis ? "Verified" : "Not Found",
          details: "Home Affairs National Identification System"
        },
        { 
          system: "On NPR", 
          status: this.on_npr_text, 
          alert: this.on_npr ? "Verified" : "Not Found",
          details: "National Population Register"
        },
        { 
          system: "Cached Result", 
          status: this.cached_result ? "Yes" : "No", 
          alert: "Info",
          details: this.cached_result ? `Cached Date: ${this.formatted_cache_date}` : "Live query result"
        },
        {
          system: "Wallet Created",
          status: this.wallet_created_text,
          alert: "Info",
          details: this.is_wallet_created ? "Digital wallet exists" : "No digital wallet"
        },
      ];
    },

    async export_data() {
      try {
        await this.load_libraries();

                const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
        
        if (this.export_format === "json") {
          this.export_json(timestamp);
        } else if (this.export_format === "csv") {
          this.export_csv(timestamp);
        } else if (this.export_format === "excel") {
          this.export_excel(timestamp);
        }

        this.close_modal();
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      }
    },

    export_json(timestamp) {
      const data = {};

      if (this.export_options.personal_information) {
        data.personal_information = this.prepare_personal_information_data();
      }

      if (this.export_options.document_details) {
        data.document_details = this.prepare_document_details_data();
      }

      if (this.export_options.verification_results) {
        data.verification_results = this.prepare_verification_results_data();
      }

      if (this.export_options.system_status) {
        data.system_status = this.prepare_system_status_data();
      }

      if (this.export_options.raw_json) {
        data.raw_data = this.safe_rsa_id_data;
      }

      // Add metadata
      data.metadata = {
        report_type: "RSA ID Verification Report",
        generated_date: this.generated_date,
        reference: this.report_reference,
        service_name: this.service_name,
        service_id: this.service_id
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rsa-id-verification-${timestamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },

    export_csv(timestamp) {
      if (this.export_options.personal_information) {
        const data = this.prepare_personal_information_data();
        const csv = window.Papa.unparse(data);
        this.download_csv(csv, `personal-information-${timestamp}.csv`);
      }

      if (this.export_options.document_details) {
        const data = this.prepare_document_details_data();
        const csv = window.Papa.unparse(data);
        this.download_csv(csv, `document-details-${timestamp}.csv`);
      }

      if (this.export_options.verification_results) {
        const data = this.prepare_verification_results_data();
        const csv = window.Papa.unparse(data);
        this.download_csv(csv, `verification-results-${timestamp}.csv`);
      }

      if (this.export_options.system_status) {
        const data = this.prepare_system_status_data();
        const csv = window.Papa.unparse(data);
        this.download_csv(csv, `system-status-${timestamp}.csv`);
      }

      if (this.export_options.raw_json) {
        const flattened = this.flatten_object(this.safe_rsa_id_data);
        const data = Object.entries(flattened).map(([key, value]) => ({
          property: key,
          value: value,
        }));
        const csv = window.Papa.unparse(data);
        this.download_csv(csv, `raw-data-${timestamp}.csv`);
      }
    },

    export_excel(timestamp) {
      const workbook = window.XLSX.utils.book_new();

      // Add metadata sheet
      const metadata = [
        { Property: "Report Type", Value: "RSA ID Verification Report" },
        { Property: "Generated Date", Value: this.generated_date },
        { Property: "Reference", Value: this.report_reference },
        { Property: "Service Name", Value: this.service_name },
        { Property: "Service ID", Value: this.service_id },
      ];
      const metadataSheet = window.XLSX.utils.json_to_sheet(metadata);
      window.XLSX.utils.book_append_sheet(workbook, metadataSheet, "Report Info");

      if (this.export_options.personal_information) {
        const data = this.prepare_personal_information_data();
        const worksheet = window.XLSX.utils.json_to_sheet(data);
        window.XLSX.utils.book_append_sheet(workbook, worksheet, "Personal Info");
      }

      if (this.export_options.document_details) {
        const data = this.prepare_document_details_data();
        const worksheet = window.XLSX.utils.json_to_sheet(data);
        window.XLSX.utils.book_append_sheet(workbook, worksheet, "Document Details");
      }

      if (this.export_options.verification_results) {
        const data = this.prepare_verification_results_data();
        const worksheet = window.XLSX.utils.json_to_sheet(data);
        window.XLSX.utils.book_append_sheet(workbook, worksheet, "Verification Results");
      }

      if (this.export_options.system_status) {
        const data = this.prepare_system_status_data();
        const worksheet = window.XLSX.utils.json_to_sheet(data);
        window.XLSX.utils.book_append_sheet(workbook, worksheet, "System Status");
      }

      if (this.export_options.raw_json) {
        const flattened = this.flatten_object(this.safe_rsa_id_data);
        const data = Object.entries(flattened).map(([key, value]) => ({
          property: key,
          value: value,
        }));
        const worksheet = window.XLSX.utils.json_to_sheet(data);
        window.XLSX.utils.book_append_sheet(workbook, worksheet, "Raw Data");
      }

      window.XLSX.writeFile(workbook, `rsa-id-verification-${timestamp}.xlsx`);
    },

    download_csv(csvContent, filename) {
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },

    flatten_object(obj, prefix = "") {
      let flattened = {};
      for (let key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const newKey = prefix ? `${prefix}.${key}` : key;
          if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
            Object.assign(flattened, this.flatten_object(obj[key], newKey));
          } else {
            flattened[newKey] = obj[key];
          }
        }
      }
      return flattened;
    },
  },
};